import { TypeOfBankTransfer } from "@/apps/tatar/invoiceConfigApp/RACInterfaces";
import ModeOfPaymentCreationForm from "@/apps/tatar/invoiceConfigApp/views/ModeOfPaymentConfig/ModeOfPaymentCreationForm";
import CDNLink from "@/components/CDNLink/CDNLink";
import CompareField from "@/components/Form/Fields/CompareField";
import FormFieldValues from "@/components/Form/Fields/FormFieldValues";
import Rerender from "@/components/Form/Helpers/Rerender";
import FormValidators, {
  FV,
} from "@/components/Form/Validation/FormValidators";
import ModalManager from "@/components/ModalComponent/ModalManager";
import { PDFHighlight } from "@/components/PDFViewer/PDFViewer";
import i18n from "@/i18n";
import { AssetTypes } from "@/model/AssetTypes";
import {
  BankAccountDataEU,
  Contact,
  OrganizationContactData,
} from "@/model/db/Contact";
import BFChooserSelect from "@/modules/abstract-ui/forms/chooser/BFChooserSelect";
import BFDatefield from "@/modules/abstract-ui/forms/datefield/BFDatefield";
import BFCurrencySelect from "@/modules/abstract-ui/forms/input/BFCurrencySelect";
import BFInput from "@/modules/abstract-ui/forms/input/BFInput";
import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import BfIcon from "@/modules/abstract-ui/icon/BfIcon";
import ContactSelect from "@/modules/contacts-module/ContactSelect";
import ContactUtils from "@/modules/contacts-module/ContactUtils";
import InvoiceStruct from "@/redux/actions/struct/implemented/InvoiceStruct";
import OrgaStruct from "@/redux/actions/struct/implemented/OrgaStruct";
import CacheService from "@/services/CacheService";
import LanguageService from "@/services/LanguageService";
import PermissionService from "@/services/PermissionService";
import ArrayUtils from "@/utils/ArrayUtils";
import ObjectIdService from "@/utils/ObjectIdUtils";
import StringUtils from "@/utils/StringUtils";
import classNames from "classnames";
import { FormApi } from "final-form";
import { Field } from "react-final-form";
import { useDispatch } from "react-redux";
import { Invoice, InvoiceKind } from "../../../RAInterfaces";
import { RA_INVOICE_MAIN_FIELDS } from "../../../RAUtils";
import { ExtractField } from "../ExtractField";
import { INVOICE_EXTRACT_KEYS } from "../RAInvoiceFormConst";
import RAInvoiceActivityField from "./RAInvoiceActivityField";
import RAInvoiceContactChange from "./RAInvoiceContactChange";
import RAInvoiceDunningPaymentFields from "./RAInvoiceDunningPaymentFields";
import RAInvoiceFormUtils from "./RAInvoiceFormUtils";
import RAInvoicePaymentFields from "./RAInvoicePaymentFields";
import RAInvoiceStornoPaymentFields from "./RAInvoiceStornoPaymentFields";
// import "./RAInvoiceDefaultFields.scss";

interface RAInvoiceDefaultFieldsProps {
  disableAll?: boolean;
  ignoreCompare?: boolean;
  prefix?: string;
  invoice: Invoice;
  form: FormApi;
  onHighlight: (highlight?: PDFHighlight | PDFHighlight[]) => void;

  uploadForm?: boolean;
  invoiceKind: InvoiceKind;
}
const RAInvoiceDefaultFields = (props: RAInvoiceDefaultFieldsProps) => {
  const direction = props.invoice?.data?.direction;
  const dispatch = useDispatch();

  const ibanSuggestions = (contactId: string) => async (search) => {
    if (!contactId) {
      return [];
    }
    const contact: Contact = await CacheService.getData({
      id: contactId,
      oType: "asset",
      assetType: AssetTypes.Contact,
      checkTables: true,
    });
    if (!contact) {
      return [];
    } else {
      return contact.data.bankAccount
        .filter((e) => e.type === "EU")
        .map((e: BankAccountDataEU) => ({
          value: e.iban,
          label: StringUtils.formatIban(e.iban),
        }));
    }
  };

  return (
    <div className={classNames("ra-invoice-default-fields")}>
      <div className={`form-row`}>
        <div className={`field __flex-1`}>
          <ExtractField
            ignoreCompare={props.ignoreCompare}
            name={`${props.prefix || ""}${
              RA_INVOICE_MAIN_FIELDS().entity.identifier
            }`}
            validate={FormValidators.compose(FormValidators.required())}
            invoice={props.invoice}
            onHighlight={props.onHighlight}
            field={
              direction === "INCOMING"
                ? INVOICE_EXTRACT_KEYS.customerCompanyName
                : INVOICE_EXTRACT_KEYS.supplierCompanyName
            }
            checkExtractValue={(value: string, extractValue: string) =>
              value === OrgaStruct.findEntityByName(extractValue)?._id
            }
            disableExtraction={(value: string) =>
              OrgaStruct.findEntityByName(value) === undefined
            }
            formatValue={(value: string) =>
              OrgaStruct.findEntityByName(value)?.displayName
            }
            onExtractValueChange={(value: string) =>
              OrgaStruct.findEntityByName(value)?._id
            }
          >
            {({ input, meta }) => (
              <BFChooserSelect
                {...FormValidators.getValidation(meta)}
                label={RA_INVOICE_MAIN_FIELDS().entity.label + "*"}
                cleanable={false}
                disabled={
                  props.invoiceKind === InvoiceKind.DUNNING ||
                  props.invoiceKind === InvoiceKind.STORNO ||
                  props.disableAll
                }
                data={ArrayUtils.sortData(
                  OrgaStruct.getEntities(props.invoice.data.type).map(
                    (entity) => ({
                      value: entity._id,
                      label: entity.displayName,
                    })
                  ),
                  { key: "label", dir: "asc" }
                )}
                {...input}
                {...FormValidators.getValidation(meta)}
                onChange={(value) => {
                  input.onChange(value);
                  props.form.mutators.setValue("objectId", null);
                }}
              />
            )}
          </ExtractField>
        </div>
        <FormFieldValues names={["entity"]}>
          {([entity]) => (
            <div className={`field __flex-1`}>
              <CompareField
                ignoreCompare={props.ignoreCompare}
                name={`${props.prefix || ""}${
                  RA_INVOICE_MAIN_FIELDS().contact.identifier
                }`}
                validate={FormValidators.compose(FormValidators.required())}
              >
                {({ input, meta }) => {
                  const initialData = getContactInitialData(props.invoice);

                  const fileCdnData = props.invoice?.data?.invoice?.file;
                  return (
                    <CDNLink
                      assetType={AssetTypes.Invoicing.Invoice}
                      assetId={props.invoice?._id}
                      assetField={"data.invoice.file"}
                      cdnId={fileCdnData.id}
                      filename={fileCdnData.filename}
                      fileKey={fileCdnData.key}
                      hasFolderReadPermissions={true}
                      render={(url) => (
                        <ContactSelect
                          disabled={
                            props.invoiceKind === InvoiceKind.DUNNING ||
                            props.invoiceKind === InvoiceKind.STORNO ||
                            props.disableAll
                          }
                          overwriteForEntity={entity}
                          creationModalHelpComponent={{
                            type: "pdfs",
                            pdfs: [
                              {
                                type: "url",
                                url: url,
                                filename: fileCdnData.filename,
                              },
                            ],
                          }}
                          businessUnits={[props.invoice.data.type]}
                          contactTypes={
                            direction === "INCOMING"
                              ? ["INVOICE"]
                              : ["BILL", "TENANT"]
                          }
                          label={
                            direction === "INCOMING"
                              ? RA_INVOICE_MAIN_FIELDS().contact.label + "*"
                              : i18n.t(
                                  "ra:InvoiceForm.BillReceiver",
                                  "Rechnungsempfänger"
                                ) + "*"
                          }
                          {...input}
                          {...FormValidators.getValidation(meta)}
                          onChange={(value, contact) => {
                            input.onChange(value);

                            RAInvoiceFormUtils.contactUpdated(
                              props.prefix || "",
                              props.form,
                              contact,
                              entity,
                              props.invoice
                            );
                          }}
                          placeholder={
                            direction === "INCOMING"
                              ? i18n.t(
                                  "ra:InvoiceForm.ContactPlaceholder",
                                  "Zahlungsempfänger auswählen"
                                )
                              : i18n.t(
                                  "ra:InvoiceForm.BillReceiverPlaceholder",
                                  "Rechnungsempfänger auswählen"
                                )
                          }
                          addText={
                            initialData?.displayName
                              ? initialData.displayName
                              : undefined
                          }
                          initialCreateObj={
                            initialData
                              ? ({
                                  data: {
                                    ...initialData,
                                    type: props.invoice.data.type,
                                    contactType:
                                      direction === "INCOMING"
                                        ? ["INVOICE"]
                                        : ["BILL"],
                                  },
                                } as any)
                              : undefined
                          }
                        />
                      )}
                    />
                  );
                }}
              </CompareField>
            </div>
          )}
        </FormFieldValues>
      </div>
      <div className={`form-row`}>
        <div className={`field __flex-1`}>
          <FormFieldValues
            names={[
              `${props.prefix || ""}${
                RA_INVOICE_MAIN_FIELDS().entity.identifier
              }`,
            ]}
          >
            {([entity]) => (
              <CompareField
                ignoreCompare={props.ignoreCompare}
                name={`${props.prefix || ""}${
                  RA_INVOICE_MAIN_FIELDS().objectId.identifier
                }`}
              >
                {({ input, meta }) => (
                  <BFChooserSelect
                    disabled={
                      props.invoiceKind === InvoiceKind.DUNNING ||
                      props.invoiceKind === InvoiceKind.STORNO ||
                      props.disableAll
                    }
                    label={RA_INVOICE_MAIN_FIELDS().objectId.label}
                    cleanable={true}
                    data={OrgaStruct.getObjectsOfEntity(entity)?.map(
                      (entity) => ({
                        value: entity._id,
                        label: `${entity.id} - ${entity.displayName}`,
                      })
                    )}
                    {...input}
                    {...FormValidators.getValidation(meta)}
                  />
                )}
              </CompareField>
            )}
          </FormFieldValues>
        </div>
        <div className={`field __flex-1`}>
          <CompareField
            ignoreCompare={props.ignoreCompare}
            name={`${props.prefix || ""}${
              RA_INVOICE_MAIN_FIELDS().invoiceType.identifier
            }`}
            validate={FormValidators.compose(FormValidators.required())}
          >
            {({ input, meta }) => (
              <BFChooserSelect
                label={RA_INVOICE_MAIN_FIELDS().invoiceType.label + "*"}
                cleanable={false}
                {...FormValidators.getValidation(meta)}
                disabled={props.disableAll}
                data={ArrayUtils.sortData(
                  InvoiceStruct.getInvoiceTypesByUnitAndDirection(
                    props.invoice.data.type,
                    props.invoice?.data?.direction
                  ).map((type) => ({
                    value: type._id,
                    label: LanguageService.translateLabel(
                      type.data.displayName
                    ),
                    sortIndex: type.data.sortIndex || 0,
                    description: type.data.description,
                  })),
                  [
                    { key: "sortIndex", dir: "asc" },
                    { key: "label", dir: "asc" },
                  ]
                )}
                {...input}
              />
            )}
          </CompareField>
        </div>
      </div>
      <div className={`form-row`}>
        <div className={`field __flex-2`}>
          <ExtractField
            ignoreCompare={props.ignoreCompare}
            name={`${props.prefix || ""}${
              RA_INVOICE_MAIN_FIELDS().invoiceId.identifier
            }`}
            validate={FormValidators.compose(FormValidators.required())}
            invoice={props.invoice}
            onHighlight={props.onHighlight}
            field={INVOICE_EXTRACT_KEYS.invoiceNumber}
          >
            {({ input, meta }) => {
              const writeInvoiceIdIntoUsages = (
                invoiceId: string,
                force = false
              ) => {
                const values = props.form.getState().values;
                const paymentPlan = values["paymentPlan"];
                const installment = values["installment"];

                props.form.batch(() => {
                  if (paymentPlan && paymentPlan.length > 0) {
                    paymentPlan.forEach((plan, index) => {
                      if (!plan.paymentDate) {
                        if (plan.usage && !force) {
                          return;
                        }
                        props.form.change(
                          `paymentPlan[${index}].usage`,
                          invoiceId
                        );
                      }
                    });
                  }
                  if (installment && installment.length > 0) {
                    installment.forEach((plan, index) => {
                      if (!plan.paymentDate) {
                        if (plan.usage && !force) {
                          return;
                        }
                        props.form.change(
                          `installment[${index}].usage`,
                          invoiceId
                        );
                      }
                    });
                  }
                });
              };
              return (
                <BFInput
                  disabled={props.disableAll}
                  label={
                    props.invoiceKind === InvoiceKind.DUNNING
                      ? i18n.t("ra:InvoiceForm.dunningId", "Mahnungsnummer") +
                        "*"
                      : RA_INVOICE_MAIN_FIELDS().invoiceId.label + "*"
                  }
                  {...FormValidators.getValidation(meta)}
                  {...input}
                  removeSuffixPadding
                  suffix={
                    <BFButton
                      // noPadding
                      tooltip={{
                        tooltip: i18n.t(
                          "ra:InvoiceForm.copyInvoiceId",
                          "Rechnungsnummer als Verwendungszweck verwenden"
                        ),
                      }}
                      appearance="link"
                      size="xxs"
                      onClick={() => {
                        writeInvoiceIdIntoUsages(input.value, true);
                      }}
                    >
                      <BfIcon type="light" data="duplicate" size="xxs" />
                    </BFButton>
                  }
                  onBlur={() => {
                    const value = input.value;
                    if (value) {
                      writeInvoiceIdIntoUsages(value);
                    }
                  }}
                />
              );
            }}
          </ExtractField>
        </div>
        <FormFieldValues names={[`${props.prefix || ""}paymentType`]}>
          {([paymentType]) => (
            <div className={`field __flex-1`}>
              <Field
                // ignoreCompare={props.ignoreCompare}
                name={`${props.prefix || ""}${
                  RA_INVOICE_MAIN_FIELDS().documentDate.identifier
                }`}
                validate={FormValidators.compose(FormValidators.required())}
                // invoice={props.invoice}
                // onHighlight={props.onHighlight}
                // field={INVOICE_EXTRACT_KEYS.}
                // checkExtractValue={(value, extractValue) =>
                //   moment(value).isSame(moment(extractValue), "day")
                // }
                formatValue={(value: string) => StringUtils.formatDate(value)}
              >
                {({ input, meta }) => (
                  <BFDatefield
                    disabled={props.disableAll}
                    label={
                      props.invoiceKind == InvoiceKind.DUNNING
                        ? i18n.t(
                            "ra:InvoiceForm.dunningDate",
                            "Mahnungsdatum"
                          ) + "*"
                        : RA_INVOICE_MAIN_FIELDS().documentDate.label + "*"
                    }
                    {...FormValidators.getValidation(meta)}
                    {...input}
                    onChange={(value) => {
                      input.onChange(value);

                      RAInvoiceFormUtils.updateInvoiceDate(
                        props.prefix,
                        props.form,
                        props.invoice,
                        value,
                        paymentType
                      );
                    }}
                  />
                )}
              </Field>
            </div>
          )}
        </FormFieldValues>
        <div className={`field __flex-1`}>
          <ExtractField
            ignoreCompare={props.ignoreCompare}
            name={`${props.prefix || ""}currency`}
            validate={FormValidators.compose(FormValidators.required())}
            invoice={props.invoice}
            onHighlight={props.onHighlight}
            defaultValue={"EUR"}
            field={INVOICE_EXTRACT_KEYS.currencyCode}
          >
            {({ input, meta }) => (
              <BFCurrencySelect
                disabled={props.disableAll}
                appearance={"input"}
                // disabled={invoicePayed}
                // readonlyAmountField={(paymentPlan || []).length > 0}
                // hint={
                //   (paymentPlan || []).length > 0
                //     ? i18n.t(
                //         "ra:InvoiceForm.calculatedByPaymentPlan",
                //         "Durch Zahlungsplan berechnet"
                //       )
                //     : undefined
                // }
                label={i18n.t("ra:InvoiceForm.currency", "Währung") + "*"}
                {...FormValidators.getValidation(meta)}
                {...input}
              />
            )}
          </ExtractField>
        </div>
      </div>

      <FormFieldValues
        names={[
          `${props.prefix || ""}entity`,
          `${props.prefix || ""}invoiceType`,
        ]}
      >
        {([entity, invoiceType]) => {
          return (
            <div className={`__flex __flex-1 connect-data-row`}>
              <div className="__field __flex-1">
                <RAInvoiceActivityField
                  ignoreCompare={props.ignoreCompare}
                  prefix={props.prefix}
                  disableAll={props.disableAll}
                  invoiceType={invoiceType}
                  invoice={props.invoice}
                  entity={entity}
                />
              </div>
            </div>
          );
        }}
      </FormFieldValues>

      {props.invoiceKind !== InvoiceKind.DUNNING &&
        props.invoiceKind !== InvoiceKind.STORNO && (
          <>
            <div className={`form-row`}>
              <FormFieldValues names={[`${props.prefix || ""}contact`]}>
                {([contact]) => (
                  <div className={`field __flex-1`}>
                    <CompareField
                      ignoreCompare={props.ignoreCompare}
                      name={`${props.prefix || ""}paymentType`}
                      validate={FV.required()}
                    >
                      {({ input, meta }) => (
                        <div className={`field  __flex-1`}>
                          <Rerender>
                            {(rerender) => (
                              <BFChooserSelect
                                disabled={props.disableAll}
                                data={InvoiceStruct.getModalOfPaymentsForChooser(
                                  direction,
                                  [props.invoice.data.type]
                                )}
                                {...input}
                                {...FV.getValidation(meta)}
                                label={
                                  i18n.t(
                                    "ContactRelationFormFields.paymentType",
                                    "Zahlungsbedingung"
                                  ) + "*"
                                }
                                overlayBottomComponent={(close) => {
                                  const addButtonDisabled =
                                    !PermissionService.hasBusinessUnitRole(
                                      `invConf_modeOfPayment`,
                                      false,
                                      false
                                    )?.includes(props.invoice.data.type);
                                  return (
                                    <div
                                      className={`__flex __align-center __justify-center __border-top`}
                                    >
                                      <BFButton
                                        appearance="link"
                                        disabled={addButtonDisabled}
                                        tooltip={
                                          addButtonDisabled
                                            ? {
                                                tooltip: i18n.t(
                                                  "Global.Labels.noPermission"
                                                ),
                                              }
                                            : undefined
                                        }
                                        onClick={() => {
                                          close();

                                          ModalManager.show({
                                            noPadding: true,
                                            size: "sm",
                                            content: (
                                              state,
                                              setState,
                                              close
                                            ) => (
                                              <ModeOfPaymentCreationForm
                                                direction={
                                                  props.invoice.data.direction
                                                }
                                                onClose={close}
                                                unit={props.invoice.data.type}
                                                onSuccess={() => {
                                                  rerender();
                                                }}
                                              />
                                            ),
                                          });
                                        }}
                                      >
                                        {i18n.t(
                                          "ContactRelationFormFields.changePaymentType",
                                          "Neue Zahlungsart"
                                        )}
                                      </BFButton>
                                    </div>
                                  );
                                }}
                                onChange={(value: string) => {
                                  input.onChange(value);

                                  RAInvoiceFormUtils.updatePaymentType(
                                    props.prefix,
                                    props.form,
                                    value,
                                    contact,
                                    props.invoice
                                  );
                                }}
                              />
                            )}
                          </Rerender>
                        </div>
                      )}
                    </CompareField>
                  </div>
                )}
              </FormFieldValues>

              <div className={`field __flex-1`}>
                <FormFieldValues names={[`${props.prefix || ""}paymentType`]}>
                  {([paymentType]) => {
                    if (!paymentType) {
                      return null;
                    }
                    const mode = InvoiceStruct.getModeOfPayment(
                      direction,
                      paymentType
                    );
                    if (mode?.kind === "bankTransfer") {
                      return (
                        <CompareField
                          ignoreCompare={props.ignoreCompare}
                          name={`${props.prefix || ""}paymentTypeData.type`}
                          validate={FV.compose(FV.required())}
                        >
                          {({ input, meta }) => (
                            <BFChooserSelect
                              disabled={props.disableAll}
                              data={Object.values(TypeOfBankTransfer).map(
                                (val) => ({
                                  label: i18n.t(
                                    `ra:Invoice.TypeOfBankTransfer.${val}`,
                                    val
                                  ),
                                  value: val,
                                })
                              )}
                              {...input}
                              {...FV.getValidation(meta)}
                              label={
                                i18n.t(
                                  "ContactRelationFormFields.typeOfBankTransfer",
                                  "Überweisungsart"
                                ) + "*"
                              }
                            />
                          )}
                        </CompareField>
                      );
                    }

                    return null;
                  }}
                </FormFieldValues>
              </div>
            </div>
            <FormFieldValues
              names={[`${props.prefix || ""}paymentTypeData.type`]}
            >
              {([typeOfBankTransfer]: [TypeOfBankTransfer]) => {
                if (typeOfBankTransfer === TypeOfBankTransfer.PAYMENT_IBAN) {
                  return (
                    <>
                      <div className={`form-row`}>
                        <div className={`field __flex-1`}>
                          <FormFieldValues
                            names={[`${props.prefix || ""}contact`]}
                          >
                            {([contact]) => (
                              <ExtractField
                                ignoreCompare={props.ignoreCompare}
                                name={`${
                                  props.prefix || ""
                                }paymentTypeData.iban`}
                                validate={FormValidators.compose(
                                  FormValidators.required(),
                                  FormValidators.iban()
                                )}
                                invoice={props.invoice}
                                onHighlight={props.onHighlight}
                                field={"bankIban"}
                                formatValue={(value: string) =>
                                  StringUtils.formatIban(value)
                                }
                              >
                                {({ input, meta }) => (
                                  <BFInput
                                    disabled={props.disableAll}
                                    {...input}
                                    type="ibanInput"
                                    label={`${i18n.t(
                                      "ra:InvoiceForm.IBAN",
                                      "IBAN"
                                    )}*`}
                                    {...FV.getValidation(meta)}
                                    suggestions={ibanSuggestions(contact)}
                                  />
                                )}
                              </ExtractField>
                            )}
                          </FormFieldValues>
                        </div>
                        <div className={`field __flex-1`}></div>
                      </div>
                    </>
                  );
                }
                if (
                  typeOfBankTransfer ===
                  TypeOfBankTransfer.PAYMENT_IBAN_INTERNATIONAL
                ) {
                  return (
                    <>
                      <div className={`form-row`}>
                        <div className={`field __flex-1`}>
                          <FormFieldValues
                            names={[`${props.prefix || ""}contact`]}
                          >
                            {([contact]) => (
                              <ExtractField
                                ignoreCompare={props.ignoreCompare}
                                name={`${
                                  props.prefix || ""
                                }paymentTypeData.iban`}
                                validate={FormValidators.compose(
                                  FormValidators.required(),
                                  FormValidators.iban()
                                )}
                                invoice={props.invoice}
                                onHighlight={props.onHighlight}
                                field={"bankIban"}
                                formatValue={(value: string) =>
                                  StringUtils.formatIban(value)
                                }
                              >
                                {({ input, meta }) => (
                                  <BFInput
                                    disabled={props.disableAll}
                                    {...input}
                                    type="ibanInput"
                                    label={`${i18n.t(
                                      "ra:InvoiceForm.IBAN",
                                      "IBAN"
                                    )}*`}
                                    {...FV.getValidation(meta)}
                                    suggestions={ibanSuggestions(contact)}
                                  />
                                )}
                              </ExtractField>
                            )}
                          </FormFieldValues>
                        </div>
                        <div className={`field __flex-1`}>
                          <ExtractField
                            ignoreCompare={props.ignoreCompare}
                            name={`${props.prefix || ""}paymentTypeData.bic`}
                            validate={FormValidators.compose(
                              FormValidators.required()
                            )}
                            invoice={props.invoice}
                            onHighlight={props.onHighlight}
                            field={"bankSwift"}
                          >
                            {({ input, meta }) => (
                              <BFInput
                                disabled={props.disableAll}
                                {...input}
                                label={`${i18n.t(
                                  "ra:InvoiceForm.BIC",
                                  "BIC"
                                )}*`}
                                {...FV.getValidation(meta)}
                              />
                            )}
                          </ExtractField>
                        </div>
                      </div>
                    </>
                  );
                }
                if (
                  typeOfBankTransfer ===
                  TypeOfBankTransfer.PAYMENT_INTERNATIONAL
                ) {
                  return (
                    <>
                      <div className={`form-row`}>
                        <div className={`field __flex-1`}>
                          <CompareField
                            ignoreCompare={props.ignoreCompare}
                            name={`${
                              props.prefix || ""
                            }paymentTypeData.account`}
                            validate={FormValidators.compose(
                              FormValidators.required()
                            )}
                          >
                            {({ input, meta }) => (
                              <BFInput
                                disabled={props.disableAll}
                                {...input}
                                label={`${i18n.t(
                                  "ra:InvoiceForm.account",
                                  "Kontonummer"
                                )}*`}
                                {...FV.getValidation(meta)}
                              />
                            )}
                          </CompareField>
                        </div>
                        <div className={`field __flex-1`}>
                          <CompareField
                            ignoreCompare={props.ignoreCompare}
                            name={`${
                              props.prefix || ""
                            }paymentTypeData.routing`}
                            validate={FormValidators.compose(
                              FormValidators.required()
                            )}
                          >
                            {({ input, meta }) => (
                              <BFInput
                                disabled={props.disableAll}
                                {...input}
                                label={`${i18n.t(
                                  "ra:InvoiceForm.routing",
                                  "RoutingNumber"
                                )}*`}
                                {...FV.getValidation(meta)}
                              />
                            )}
                          </CompareField>
                        </div>
                        <div className={`field __flex-1`}>
                          <CompareField
                            ignoreCompare={props.ignoreCompare}
                            name={`${props.prefix || ""}paymentTypeData.swift`}
                            validate={FormValidators.compose(
                              FormValidators.required()
                            )}
                          >
                            {({ input, meta }) => (
                              <BFInput
                                disabled={props.disableAll}
                                {...input}
                                label={`${i18n.t(
                                  "ra:InvoiceForm.swift",
                                  "SWIFT"
                                )}*`}
                                {...FV.getValidation(meta)}
                              />
                            )}
                          </CompareField>
                        </div>
                      </div>
                      {/* <div className={`form-row`}>
                        <div className={`field __flex-1`}>
                          <CompareField
                            ignoreCompare={props.ignoreCompare}
                            name={`${
                              props.prefix || ""
                            }paymentTypeData.nameOfBank`}
                            validate={FormValidators.compose(
                              FormValidators.required()
                            )}
                          >
                            {({ input, meta }) => (
                              <BFInput
                                disabled={props.disableAll}
                                {...input}
                                label={`${i18n.t(
                                  "ra:InvoiceForm.nameOfBank",
                                  "Name der Bank"
                                )}*`}
                                {...FV.getValidation(meta)}
                              />
                            )}
                          </CompareField>
                        </div>
                        <div className={`field __flex-2`}>
                          <CompareField
                            ignoreCompare={props.ignoreCompare}
                            name={`${
                              props.prefix || ""
                            }paymentTypeData.addressOfBank`}
                            validate={FormValidators.compose(
                              FormValidators.required()
                            )}
                          >
                            {({ input, meta }) => (
                              <BFInput
                                {...input}
                                disabled={props.disableAll}
                                label={`${i18n.t(
                                  "ra:InvoiceForm.addressOfBank",
                                  "Adresse der Bank"
                                )}*`}
                                {...FV.getValidation(meta)}
                              />
                            )}
                          </CompareField>
                        </div>
                      </div> */}
                    </>
                  );
                }

                return null;
              }}
            </FormFieldValues>
          </>
        )}

      {props.invoiceKind !== InvoiceKind.DUNNING &&
        props.invoiceKind !== InvoiceKind.STORNO && (
          <RAInvoicePaymentFields {...props} />
        )}
      {props.invoiceKind === InvoiceKind.DUNNING && (
        <RAInvoiceDunningPaymentFields {...props} />
      )}
      {props.invoiceKind === InvoiceKind.STORNO && (
        <RAInvoiceStornoPaymentFields {...props} />
      )}

      <RAInvoiceContactChange form={props.form} invoice={props.invoice} />
    </div>
  );
};

export default RAInvoiceDefaultFields;

const getContactInitialData = (invoice: Invoice) => {
  const direction = invoice?.data?.direction;
  const contact: Partial<OrganizationContactData> = {
    personType: "organization",
  };
  let added = false;
  const name =
    direction === "INCOMING"
      ? invoice.extract?.supplierCompanyName
      : invoice.extract?.customerCompanyName;
  if (name) {
    contact.displayName = name;
    contact.companyName = name;
    contact.salutationText = ContactUtils.getSalutationString({
      personType: contact.personType,
    });
    added = true;
  }

  const address =
    direction === "INCOMING"
      ? invoice.extract?.supplierAddress
      : invoice.extract?.customerAddress;
  if (address) {
    contact.address = [
      {
        id: ObjectIdService.new(),
        isMain: true,
        street: address?.street,
        additional: "",
        city: address?.city,
        zip: address?.postalCode,
        country: address?.country,
      },
    ];
    added = true;
  }

  const phone =
    direction === "INCOMING" ? invoice.extract?.supplierPhoneNumber : null;

  if (phone) {
    contact.phone = [
      {
        id: ObjectIdService.new(),
        note: "",
        phone: phone,
      },
    ];
    added = true;
  }

  const email =
    direction === "INCOMING" ? invoice.extract?.supplierEmail : null;
  if (email) {
    contact.emails = [
      {
        id: ObjectIdService.new(),
        email: email,
        note: "",
      },
    ];
    added = true;
  }

  // TODO - should we add website for contact?
  // const website = invoice.extract.find(
  //   (e) => e.identifier === INVOICE_EXTRACT_KEYS.supplierWebsite
  // );
  // if(website) {
  //   contact.website = website.value;
  //   added = true;
  // }
  if (added) {
    return contact;
  } else {
    return null;
  }
};
